// src/lib/http/client.ts
// The one browser HTTP client (contract §6.10, S45): same-origin paths, credentials only as HttpOnly cookies, never an Authorization header.

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";

// The default axios instance is the shared client, so every module and every test mock of "axios" sees the same object.
export const http: AxiosInstance = axios;

export interface ServiceClientDefaults {
	baseURL?: string;
	headers?: Record<string, string>;
}

type Config = AxiosRequestConfig;

// What modules that once built their own axios.create() instance call; every request still goes through `http`.
export interface ServiceClient {
	get<T = any, R = AxiosResponse<T>>(url: string, config?: Config): Promise<R>;
	delete<T = any, R = AxiosResponse<T>>(url: string, config?: Config): Promise<R>;
	head<T = any, R = AxiosResponse<T>>(url: string, config?: Config): Promise<R>;
	post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: Config): Promise<R>;
	put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: Config): Promise<R>;
	patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: Config): Promise<R>;
	request<T = any, R = AxiosResponse<T>>(config: Config): Promise<R>;
}

export const createServiceClient = (defaults: ServiceClientDefaults = {}): ServiceClient => {
	const merge = (config: Config = {}): Config => ({
		...config,
		baseURL: config.baseURL ?? defaults.baseURL,
		headers: { ...(defaults.headers ?? {}), ...((config.headers as Record<string, string>) ?? {}) },
	});
	return {
		get: (url, config) => http.get(url, merge(config)),
		delete: (url, config) => http.delete(url, merge(config)),
		head: (url, config) => http.head(url, merge(config)),
		post: (url, data, config) => http.post(url, data, merge(config)),
		put: (url, data, config) => http.put(url, data, merge(config)),
		patch: (url, data, config) => http.patch(url, data, merge(config)),
		request: (config) => http.request(merge(config)),
	};
};

// axios's own buildFullPath: a relative url is appended to baseURL, an absolute one stands alone.
export const fullRequestUrl = (url = "", baseURL?: string): string => {
	if (!baseURL || /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) return url;
	return url ? `${baseURL.replace(/\/?\/$/, "")}/${url.replace(/^\/+/, "")}` : baseURL;
};

const resolve = (url: string | undefined, baseURL?: string): URL | null => {
	if (typeof window === "undefined") return null;
	try {
		return new URL(fullRequestUrl(url, baseURL), window.location.href);
	} catch {
		return null;
	}
};

// Relative paths and this page's own origin are ours; anything else (a vendor API) never gets the session treatment.
export const isSameOriginUrl = (url: string | undefined, baseURL?: string): boolean =>
	resolve(url, baseURL)?.origin === (typeof window === "undefined" ? undefined : window.location.origin);

// The path (no query) a request resolves to, for matching the §6.10 exclusions.
export const requestPath = (url: string | undefined, baseURL?: string): string => resolve(url, baseURL)?.pathname ?? url ?? "";
